export const money = (v, currency = "USD") => {
  if (v == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(v);
};

// Converts `amount` in `currency` to the FX doc's base currency using live/fallback rates.
// `ratesDoc` is the shape returned by GET /crm/exchange-rates: { base, rates: { CUR: unitsPerBase } }.
export const toBase = (amount, currency, ratesDoc) => {
  if (amount == null) return null;
  const base = ratesDoc?.base || "USD";
  if (currency === base) return amount;
  const rate = ratesDoc?.rates?.[currency];
  if (!rate) return amount;
  return amount / rate;
};

export const initials = (name = "") =>
  name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

export const relative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const dateFmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

export const daysUntil = (iso) =>
  iso ? Math.round((new Date(iso).getTime() - Date.now()) / 86400000) : null;

// Status color families -> tailwind classes (badge)
const FAMILIES = {
  green: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-400 dark:border-emerald-500/30",
  red: "bg-red-100 text-red-700 border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/30",
  amber: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-400 dark:border-amber-500/30",
  blue: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-400 dark:border-blue-500/30",
  violet: "bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-500/15 dark:text-violet-400 dark:border-violet-500/30",
  slate: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-700/40 dark:text-slate-300 dark:border-slate-600",
};

const STATUS_MAP = {
  // green
  won: "green", paid: "green", approved: "green", active: "green", resolved: "green",
  healthy: "green", accepted: "green", done: "green", completed: "green",
  // red
  lost: "red", overdue: "red", error: "red", critical: "red", rejected: "red",
  expired: "red", closed: "red", blocked: "red",
  // amber
  pending: "amber", expiring: "amber", warning: "amber", negotiation: "amber",
  probation: "amber", "pending approval": "amber", pending_approval: "amber", routed: "amber",
  draft: "amber", high: "amber", "action needed": "amber",
  // blue
  "in progress": "blue", "in_progress": "blue", new: "blue", sent: "blue",
  qualified: "blue", proposal: "blue", info: "blue", open: "blue", upcoming: "blue",
  // violet
  epic: "violet", repeat: "violet", "in pm": "violet",
};

export const statusFamily = (status = "") => STATUS_MAP[String(status).toLowerCase()] || "slate";
export const statusClass = (status) => FAMILIES[statusFamily(status)];
export const familyClass = (fam) => FAMILIES[fam] || FAMILIES.slate;

export const HEALTH_DOT = {
  green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500",
};

export const PRIORITY_DOT = {
  critical: "bg-red-500", high: "bg-amber-500", medium: "bg-blue-500", low: "bg-slate-400",
};

export const TYPE_COLOR = {
  Epic: "violet", Module: "green", Task: "slate", Subtask: "slate", Bug: "red", Feature: "blue", Activity: "amber",
};
